import React, { useMemo, useState } from "react"
import { toast } from "react-toastify"
import Select, { SingleValue } from "react-select"
import {
  Button,
  Col,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from "reactstrap"
import { BMD_SECONDS_PER_QUESTION } from "../../../global"
import {
  BMD_COPY,
  BMD_DEFAULT_TIME_BUDGET_MINUTES,
  BMD_STRAND_SELECTION_ENABLED,
  BMD_TIME_BUDGET_OPTIONS,
} from "../constants/bmd.constants"
import { useBmdStrands } from "../hooks/useBmdStrands"
import { useBmdTopics } from "../hooks/useBmdTopics"
import { useBmdYearLevels } from "../hooks/useBmdYearLevels"
import type {
  BmdStagedDraft,
  BmdTopic,
  BmdYearLevelOption,
} from "../types/bmd.types"
import { BmdPreviewModal } from "./BmdPreviewModal"
import { BmdRulesCard } from "./BmdRulesCard"
import { BmdStrandPicker, strandKey } from "./BmdStrandPicker"
import { BmdTopicList } from "./BmdTopicList"
import { BMD_COLOURS } from "../constants/bmd.colours"

type BmdTimeBudgetOpt = {
  value: string // label, used as a stable react-select key
  label: string
  minutes: number | null
}

const TIME_BUDGET_SELECT_OPTIONS: BmdTimeBudgetOpt[] =
  BMD_TIME_BUDGET_OPTIONS.map((o) => ({
    value: o.label,
    label: o.label,
    minutes: o.minutes,
  }))

// ---------------------------------------------------------------------------
// Design tokens, from the PM's mockup. Shared with BmdStrandPicker by sight
// rather than by import: the picker is a leaf and does not need this module.
// ---------------------------------------------------------------------------

const INK = BMD_COLOURS.INK
// A step darker than they started. The QA kept reporting the small grey text
// across the Add Activity tabs as unreadable, so both moved together and the
// sibling tabs match BmdPrereqTabPanel. MUTED is still a clear step off INK,
// so the states that use it to mean "absent" or "disabled" still read that way.
const BODY = BMD_COLOURS.BODY
const MUTED = BMD_COLOURS.MUTED
const RULE = BMD_COLOURS.RULE
const ACCENT = BMD_COLOURS.ACCENT
const CARD: React.CSSProperties = {
  backgroundColor: "#fff",
  border: "1px solid #e9edf3",
  borderRadius: 10,
  padding: 20,
}

const LABEL: React.CSSProperties = {
  color: INK,
  fontSize: 12.5,
  fontWeight: 700,
  marginBottom: 8,
  display: "block",
}

const HELPER: React.CSSProperties = {
  color: MUTED,
  fontSize: 11.5,
  lineHeight: 1.5,
}

/** Both selects on the settings row, so they are the same height and size. */
const SELECT_STYLES = {
  menuPortal: (base: Record<string, unknown>) => ({ ...base, zIndex: 9999 }),
  control: (base: Record<string, unknown>) => ({
    ...base,
    minHeight: "40px",
    fontSize: "13px",
  }),
}

/** "16 to 30 minutes" -> "16-30 min", to fit on the summary pill. */
function shortAllowance(label: string): string {
  return label.replace(" to ", "–").replace(" minutes", " min")
}

/** "2 clusters" - the number bold, the noun not. */
const Tally: React.FC<{ value: number; noun: string }> = ({ value, noun }) => (
  <span style={{ color: BODY, fontSize: 12.5 }}>
    <strong style={{ color: INK }}>{value}</strong> {noun}
    {value === 1 ? "" : "s"}
  </span>
)

const DEFAULT_BUDGET_OPTION: BmdTimeBudgetOpt =
  TIME_BUDGET_SELECT_OPTIONS.find(
    (o) => o.minutes === BMD_DEFAULT_TIME_BUDGET_MINUTES,
  ) || TIME_BUDGET_SELECT_OPTIONS[TIME_BUDGET_SELECT_OPTIONS.length - 1]

type BmdTabPanelProps = {
  subjectId: number
  studentKey: string
  onStageDraft: (draft: BmdStagedDraft) => void
}

export function BmdTabPanel({
  subjectId,
  studentKey,
  onStageDraft,
}: BmdTabPanelProps) {
  const {
    yearLevels,
    loading: yearLevelsLoading,
    error: yearLevelsError,
  } = useBmdYearLevels(subjectId, studentKey)

  const [selectedYear, setSelectedYear] = useState<BmdYearLevelOption | null>(
    null,
  )

  const flagOn = BMD_STRAND_SELECTION_ENABLED
  const yearId = selectedYear?.value ?? 0

  // Both hooks run on every render for the rules of hooks. Each short-circuits
  // on yearLevelId 0, so only the active mode hits the network and the rollback
  // flag stays a render-time switch.
  const {
    topics,
    lockedTopicIds: legacyLockedTopicIds,
    loading: topicsLoading,
    error: topicsError,
  } = useBmdTopics(flagOn ? 0 : yearId, subjectId, studentKey)

  const {
    strands,
    lockedTopicIds: strandLockedTopicIds,
    loading: strandsLoading,
    error: strandsError,
  } = useBmdStrands(flagOn ? yearId : 0, subjectId, studentKey)

  const lockedTopicIds = flagOn ? strandLockedTopicIds : legacyLockedTopicIds

  const [selectedTopicIds, setSelectedTopicIds] = useState<Set<number>>(
    new Set(),
  )
  const [selectedStrandKeys, setSelectedStrandKeys] = useState<Set<number>>(
    new Set(),
  )

  const [isPreviewOpen, setIsPreviewOpen] = useState<boolean>(false)
  const [previewTopicIds, setPreviewTopicIds] = useState<number[]>([])

  // Soft time-budget guardrail. null minutes = no limit.
  const [budgetOption, setBudgetOption] = useState<BmdTimeBudgetOpt>(
    DEFAULT_BUDGET_OPTION,
  )
  // A test that runs over the chosen budget routes through this confirmation
  // modal instead of staging directly.
  const [overBudgetModalOpen, setOverBudgetModalOpen] =
    useState<boolean>(false)
  const [pendingDraft, setPendingDraft] = useState<BmdStagedDraft | null>(null)

  const yearOptions: BmdYearLevelOption[] = useMemo(
    () =>
      yearLevels.map((yl) => ({
        value: yl.activity_level_id,
        label: yl.level_name,
        level_order: yl.level_order,
      })),
    [yearLevels],
  )

  // The topics that will end up in the draft. The summary, preview, estimated
  // time, and createAssignment payload all derive from this.
  const effectiveSelectedTopics: BmdTopic[] = useMemo(() => {
    if (flagOn) {
      const out: BmdTopic[] = []
      const seen = new Set<number>()
      for (const strand of strands) {
        const key = strandKey(strand)
        if (!selectedStrandKeys.has(key)) continue
        for (const t of strand.topics) {
          if (lockedTopicIds.has(t.main_topic_id)) continue
          if (seen.has(t.main_topic_id)) continue
          seen.add(t.main_topic_id)
          out.push(t)
        }
      }
      return out
    }
    return topics.filter((t) => selectedTopicIds.has(t.main_topic_id))
  }, [
    flagOn,
    strands,
    selectedStrandKeys,
    topics,
    selectedTopicIds,
    lockedTopicIds,
  ])

  // Sequential unlock: the cluster at sorted position i is unlocked if i is 0,
  // or it was previously assigned, or every lower-order cluster qualifies
  // (previously assigned or selected in this draft).
  // The synthetic "Unmapped" bucket (strand_id null) sits at the tail. It is
  // always unlocked and never counts toward the prefix.
  const unlockedStrandKeys = useMemo(() => {
    if (!flagOn) return new Set<number>()
    const ordered = [...strands].sort((a, b) => a.order - b.order)
    const unlocked = new Set<number>()
    let prefixOk = true
    for (let i = 0; i < ordered.length; i++) {
      const s = ordered[i]
      const key = strandKey(s)
      if (s.strand_id === null) {
        unlocked.add(key)
        continue
      }
      if (i === 0 || prefixOk || s.previously_assigned) {
        unlocked.add(key)
      }
      const qualifies = s.previously_assigned || selectedStrandKeys.has(key)
      prefixOk = prefixOk && qualifies
    }
    return unlocked
  }, [flagOn, strands, selectedStrandKeys])

  const previousStrandNameByKey = useMemo(() => {
    const map = new Map<number, string>()
    if (!flagOn) return map
    const ordered = [...strands].sort((a, b) => a.order - b.order)
    for (let i = 1; i < ordered.length; i++) {
      map.set(strandKey(ordered[i]), ordered[i - 1].strand_name)
    }
    return map
  }, [flagOn, strands])

  const summary = useMemo(() => {
    return {
      topic_count: effectiveSelectedTopics.length,
      total_questions: effectiveSelectedTopics.reduce(
        (sum, t) => sum + t.question_count,
        0,
      ),
      topic_names: effectiveSelectedTopics.map((t) => t.display_name),
    }
  }, [effectiveSelectedTopics])

  const selectedStrandNames = useMemo(() => {
    if (!flagOn) return []
    return strands
      .filter(
        (s) =>
          selectedStrandKeys.has(strandKey(s)) && s.strand_id !== null,
      )
      .map((s) => s.strand_name)
  }, [flagOn, strands, selectedStrandKeys])

  /** Drops the ticked clusters but keeps the year, so the list stays on screen. */
  const clearSelection = () => {
    setSelectedTopicIds(new Set())
    setSelectedStrandKeys(new Set())
  }

  const resetDraftState = () => {
    setSelectedYear(null)
    setSelectedTopicIds(new Set())
    setSelectedStrandKeys(new Set())
  }

  const handleYearChange = (option: SingleValue<BmdYearLevelOption>) => {
    setSelectedYear(option || null)
    setSelectedTopicIds(new Set())
    setSelectedStrandKeys(new Set())
  }

  const handleToggleTopic = (mainTopicId: number) => {
    setSelectedTopicIds((prev) => {
      const next = new Set(prev)
      if (next.has(mainTopicId)) next.delete(mainTopicId)
      else next.add(mainTopicId)
      return next
    })
  }

  // Every cluster the tutor could tick right now. Locked ones are excluded:
  // the sequential unlock rule is the point of them, and a "select all" that
  // quietly bypassed it would make the lock decorative.
  const selectableStrandKeys = useMemo(() => {
    if (!flagOn) return [] as number[]
    return strands
      .filter((strand) => {
        const key = strandKey(strand)
        if (!unlockedStrandKeys.has(key)) return false
        const included = strand.topics.filter(
          (t) => !lockedTopicIds.has(t.main_topic_id),
        )
        return !(strand.topics.length > 0 && included.length === 0)
      })
      .map(strandKey)
  }, [flagOn, strands, unlockedStrandKeys, lockedTopicIds])

  const allAvailableSelected =
    selectableStrandKeys.length > 0 &&
    selectableStrandKeys.every((k) => selectedStrandKeys.has(k))

  const toggleSelectAllAvailable = () => {
    setSelectedStrandKeys(
      allAvailableSelected ? new Set() : new Set(selectableStrandKeys),
    )
  }

  const handleToggleStrand = (key: number) => {
    setSelectedStrandKeys((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const openPreviewForCurrentSelection = () => {
    setPreviewTopicIds(
      effectiveSelectedTopics.map((t) => t.main_topic_id),
    )
    setIsPreviewOpen(true)
  }

  const openPreviewForYearLevel = () => {
    // Preview the whole year level: empty topic filter on the API.
    setPreviewTopicIds([])
    setIsPreviewOpen(true)
  }

  const openPreviewForSingleTopic = (mainTopicId: number) => {
    setPreviewTopicIds([mainTopicId])
    setIsPreviewOpen(true)
  }

  const openPreviewForStrand = (_key: number, includedTopicIds: number[]) => {
    setPreviewTopicIds(includedTopicIds)
    setIsPreviewOpen(true)
  }

  const closePreview = () => setIsPreviewOpen(false)

  const estimatedMinutes = Math.ceil(
    (summary.total_questions * BMD_SECONDS_PER_QUESTION) / 60,
  )
  const timeBudgetMinutes = budgetOption.minutes
  const isOverBudget =
    timeBudgetMinutes != null && estimatedMinutes > timeBudgetMinutes

  const buildDraft = (): BmdStagedDraft | null => {
    if (!selectedYear || summary.topic_count === 0) return null
    return {
      draft_id:
        Date.now().toString(36) + Math.random().toString(36).slice(2, 8),
      subject_id: subjectId,
      year_level_id: selectedYear.value,
      year_level_label: selectedYear.label,
      topic_ids: effectiveSelectedTopics.map((t) => t.main_topic_id),
      topic_names: effectiveSelectedTopics.map((t) => t.display_name),
      topic_count: summary.topic_count,
      question_count: summary.total_questions,
      force_new: false,
      strand_names: flagOn ? selectedStrandNames : undefined,
    }
  }

  const commitDraft = (draft: BmdStagedDraft) => {
    onStageDraft(draft)
    const groupingNoun =
      flagOn && draft.strand_names && draft.strand_names.length > 0
        ? `cluster${draft.strand_names.length === 1 ? "" : "s"}`
        : `topic${draft.topic_count === 1 ? "" : "s"}`
    const groupingCount =
      flagOn && draft.strand_names && draft.strand_names.length > 0
        ? draft.strand_names.length
        : draft.topic_count
    // "Added to Assigned Diagnostics", not "Staged": the button says Add, and
    // this is only the first of two steps. Nothing reaches the session until
    // the page's Add Activities button is pressed, exactly as with online
    // activities and worksheets, so the toast has to name where it went rather
    // than imply the job is done.
    toast.success(
      `Added to Assigned Diagnostics: ${groupingCount} ${groupingNoun} · ${
        draft.question_count
      } question${draft.question_count === 1 ? "" : "s"}`,
      { autoClose: 2500 },
    )
    resetDraftState()
  }

  const handleStageDraft = () => {
    const draft = buildDraft()
    if (!draft) return
    if (isOverBudget) {
      setPendingDraft(draft)
      setOverBudgetModalOpen(true)
      return
    }
    commitDraft(draft)
  }

  const handleConfirmOverBudget = () => {
    if (pendingDraft) commitDraft(pendingDraft)
    setPendingDraft(null)
    setOverBudgetModalOpen(false)
  }

  const handleCancelOverBudget = () => {
    setPendingDraft(null)
    setOverBudgetModalOpen(false)
  }

  // Both "Add to assignment" buttons - the one in the cluster header and the
  // one in the summary bar - read this, so they can never disagree about
  // whether there is anything to add.
  const canStage = !!selectedYear && summary.topic_count > 0

  const middleLoading = flagOn ? strandsLoading : topicsLoading
  const middleError = flagOn ? strandsError : topicsError

  return (
    <div style={{ padding: "10px 16px" }}>
      <BmdRulesCard />

      {yearLevelsError ? (
        <div className="alert alert-warning font-size-12 py-2">
          {yearLevelsError}
        </div>
      ) : null}

      {/* Year, time allowance, and a preview of the whole year level. */}
      <div style={{ ...CARD, marginBottom: 16 }}>
        <Row className="align-items-start">
          <Col md={4}>
            <label style={LABEL} htmlFor="bmd-year-level">
              {BMD_COPY.YEAR_SELECTOR_LABEL}
            </label>
            <Select<BmdYearLevelOption, false>
              inputId="bmd-year-level"
              value={selectedYear}
              onChange={handleYearChange}
              options={yearOptions}
              isLoading={yearLevelsLoading}
              isDisabled={yearLevelsLoading || yearLevels.length === 0}
              placeholder={BMD_COPY.YEAR_SELECTOR_PLACEHOLDER}
              classNamePrefix="select2-selection"
              menuPortalTarget={
                typeof document !== "undefined" ? document.body : null
              }
              menuPosition="fixed"
              styles={SELECT_STYLES}
            />
            <div style={{ ...HELPER, marginTop: 8 }}>
              {!yearLevelsLoading && yearLevels.length === 0
                ? BMD_COPY.EMPTY_YEAR_LEVELS
                : BMD_COPY.YEAR_SELECTOR_HINT}
            </div>
          </Col>

          <Col md={4} className="mt-3 mt-md-0">
            <label style={LABEL} htmlFor="bmd-time-budget">
              {BMD_COPY.TIME_BUDGET_LABEL}
            </label>
            <Select<BmdTimeBudgetOpt, false>
              inputId="bmd-time-budget"
              value={budgetOption}
              onChange={(opt) => setBudgetOption(opt || DEFAULT_BUDGET_OPTION)}
              options={TIME_BUDGET_SELECT_OPTIONS}
              isSearchable={false}
              classNamePrefix="select2-selection"
              menuPortalTarget={
                typeof document !== "undefined" ? document.body : null
              }
              menuPosition="fixed"
              styles={SELECT_STYLES}
            />
            <div style={{ ...HELPER, marginTop: 8 }}>
              {BMD_COPY.TIME_BUDGET_HINT}
            </div>
          </Col>

          <Col
            md={4}
            className="mt-3 mt-md-0 d-flex justify-content-md-end align-items-start"
          >
            {/* Named with the year, because "Preview" alone reads as a preview
                of the current selection, which is the other button entirely. */}
            <button
              type="button"
              className="btn d-inline-flex align-items-center gap-2"
              style={{
                backgroundColor: "#fff",
                border: `1px solid ${selectedYear ? ACCENT : "#e4e7ee"}`,
                color: selectedYear ? ACCENT : MUTED,
                borderRadius: 6,
                padding: "9px 18px",
                fontSize: 13,
                fontWeight: 600,
                whiteSpace: "nowrap",
              }}
              onClick={openPreviewForYearLevel}
              disabled={!selectedYear}
            >
              <i className="mdi mdi-eye-outline" style={{ fontSize: 15 }}></i>
              Preview all {selectedYear ? selectedYear.label : "year"} questions
            </button>
          </Col>
        </Row>
      </div>

      {/* "Cluster" is the tutor-facing name for what the data layer calls a
          strand. */}
      {selectedYear ? (
        <div style={{ ...CARD, padding: 0, marginBottom: 16 }}>
            <div
              className="d-flex align-items-center justify-content-between"
              style={{ padding: "16px 14px 12px" }}
            >
              <span style={{ color: INK, fontSize: 15, fontWeight: 700 }}>
                {flagOn ? "Clusters" : "Topics"} for {selectedYear.label}
              </span>
              <div className="d-flex align-items-center gap-3">
                {/* Selects every cluster the unlock rule currently allows,
                    which is not necessarily all of them. */}
                <button
                  type="button"
                  className="btn btn-link p-0"
                  style={{ color: ACCENT, fontSize: 12.5, textDecoration: "none" }}
                  onClick={toggleSelectAllAvailable}
                  disabled={!flagOn || selectableStrandKeys.length === 0}
                >
                  {allAvailableSelected ? "Clear all" : "Select all available"}
                </button>
                {/* The same action as the summary bar at the foot of the page.
                    A tutor who has finished picking at the top of a long
                    cluster list should not have to scroll past it to commit.
                    Disabled rather than hidden, so its position does not move
                    as clusters are ticked. */}
                <button
                  type="button"
                  className="btn fw-bold"
                  style={{
                    backgroundColor: canStage ? ACCENT : "#e9ecf1",
                    border: `1px solid ${canStage ? ACCENT : "#e9ecf1"}`,
                    color: canStage ? "#fff" : MUTED,
                    borderRadius: 6,
                    padding: "7px 16px",
                    fontSize: 12.5,
                  }}
                  disabled={!canStage}
                  onClick={handleStageDraft}
                >
                  Add to assignment
                </button>
              </div>
            </div>

            {middleLoading ? (
              <p className="text-muted mb-0 font-size-12 px-3 pb-3">
                Loading {flagOn ? "clusters" : "topics"}…
              </p>
            ) : null}

            {middleError && !middleLoading ? (
              <p className="text-danger mb-0 font-size-12 px-3 pb-3">
                Could not load {flagOn ? "clusters" : "topics"}: {middleError}
              </p>
            ) : null}

            {!middleLoading && !middleError ? (
              flagOn ? (
                <BmdStrandPicker
                  strands={strands}
                  selectedStrandKeys={selectedStrandKeys}
                  unlockedStrandKeys={unlockedStrandKeys}
                  lockedTopicIds={lockedTopicIds}
                  previousStrandNameByKey={previousStrandNameByKey}
                  onToggleStrand={handleToggleStrand}
                  onPreviewStrand={openPreviewForStrand}
                />
              ) : (
                <BmdTopicList
                  topics={topics}
                  selectedTopicIds={selectedTopicIds}
                  lockedTopicIds={lockedTopicIds}
                  onToggleTopic={handleToggleTopic}
                  onPreviewTopic={openPreviewForSingleTopic}
                />
              )
            ) : null}
        </div>
      ) : null}

      {/* Summary bar. One line: what is selected, whether it fits the
          allowance, and the two ways out. */}
      {selectedYear && summary.topic_count > 0 ? (
        <div style={{ ...CARD, marginBottom: 16 }}>
          <div className="d-flex align-items-center flex-wrap gap-3">
            <span style={{ color: INK, fontSize: 13, fontWeight: 700 }}>
              {selectedYear.label}
            </span>
            <span style={{ width: 1, height: 18, backgroundColor: RULE }} />

            <Tally
              value={flagOn ? selectedStrandNames.length : summary.topic_count}
              noun={flagOn ? "cluster" : "topic"}
            />
            {flagOn ? (
              <Tally value={summary.topic_count} noun="topic" />
            ) : null}
            <Tally value={summary.total_questions} noun="question" />

            {/* Green when it fits, amber when it does not. The tutor can still
                proceed either way - the modal asks, it does not block. */}
            <span
              className="d-inline-flex align-items-center gap-2"
              style={{
                backgroundColor: isOverBudget ? "#fdf0dc" : "#e4f5ec",
                color: isOverBudget ? "#8a5a00" : "#1c6b45",
                borderRadius: 20,
                padding: "5px 12px",
                fontSize: 12,
              }}
            >
              <i
                className={`mdi ${
                  isOverBudget ? "mdi-alert-outline" : "mdi-check"
                }`}
                style={{ fontSize: 13 }}
                aria-hidden="true"
              ></i>
              ~{estimatedMinutes} min,{" "}
              {timeBudgetMinutes == null
                ? "no limit set"
                : `${isOverBudget ? "over" : "within"} your ${shortAllowance(
                    budgetOption.label,
                  )} allowance`}
            </span>

            <div className="d-flex align-items-center gap-2 ms-auto">
              <button
                type="button"
                className="btn btn-link p-0"
                style={{ color: MUTED, fontSize: 12.5, textDecoration: "none" }}
                onClick={clearSelection}
              >
                Clear
              </button>
              {flagOn ? (
                <button
                  type="button"
                  className="btn"
                  style={{
                    backgroundColor: "#fff",
                    border: `1px solid ${ACCENT}`,
                    color: ACCENT,
                    borderRadius: 6,
                    padding: "9px 18px",
                    fontSize: 13,
                    fontWeight: 600,
                  }}
                  onClick={openPreviewForCurrentSelection}
                >
                  Preview selection
                </button>
              ) : null}
              <button
                type="button"
                className="btn fw-bold"
                style={{
                  backgroundColor: ACCENT,
                  border: `1px solid ${ACCENT}`,
                  color: "#fff",
                  borderRadius: 6,
                  padding: "9px 18px",
                  fontSize: 13,
                }}
                onClick={handleStageDraft}
              >
                Add to assignment
              </button>
            </div>
          </div>

          <div style={{ ...HELPER, marginTop: 10, textAlign: "right" }}>
            Added clusters appear under <strong>Assigned Diagnostics</strong> on
            the right, and are confirmed with <strong>Add Activities</strong>.
          </div>
        </div>
      ) : null}

      {selectedYear ? (
        <BmdPreviewModal
          isOpen={isPreviewOpen}
          onClose={closePreview}
          yearLevelId={selectedYear.value}
          yearLevelLabel={selectedYear.label}
          subjectId={subjectId}
          topicIds={previewTopicIds}
        />
      ) : null}

      {/* Over-budget confirmation. The tutor can always proceed. */}
      <Modal
        isOpen={overBudgetModalOpen}
        toggle={handleCancelOverBudget}
        centered
      >
        <ModalHeader toggle={handleCancelOverBudget}>
          {BMD_COPY.OVER_BUDGET_TITLE}
        </ModalHeader>
        <ModalBody>
          <p className="mb-0">
            This planned diagnostics test is about{" "}
            <strong>{estimatedMinutes} minutes</strong>
            {pendingDraft
              ? ` (${pendingDraft.topic_count} topic${
                  pendingDraft.topic_count === 1 ? "" : "s"
                }, ${pendingDraft.question_count} question${
                  pendingDraft.question_count === 1 ? "" : "s"
                })`
              : ""}
            , which is over your selected{" "}
            <strong>{timeBudgetMinutes}-minute</strong> budget. Consider
            trimming topics, or proceed if this is the right scope.
          </p>
        </ModalBody>
        <ModalFooter>
          <Button color="light" size="sm" onClick={handleCancelOverBudget}>
            {BMD_COPY.OVER_BUDGET_CANCEL}
          </Button>
          <Button color="danger" size="sm" onClick={handleConfirmOverBudget}>
            {BMD_COPY.OVER_BUDGET_CONFIRM}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  )
}

export default BmdTabPanel
